// Shared chrome for the full-screen, pre-workspace screens: sign in, the 6-digit
// code, set-a-new-password, and first-login intake.
//
// Moved out of LoginScreen.tsx, where it was module-private, when WelcomeScreen
// needed the same lockup, card, fields and CTA. That is the whole visual language
// of these screens rather than an overlap — duplicating it would guarantee the
// two drift.
import { type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { Icon } from './primitives';
import { Field as FormField, TextInput } from './field';

// The rule LoginScreen and SecurityScreen were each hardcoding as a bare `8`.
// Deliberately NOT config.toml's minimum_password_length of 6: that file is
// local-dev only (deploy.yml never runs `supabase config push`), and 8 is what
// both shipped screens already enforce.
export const MIN_PASSWORD = 8;

/** Which field a password form's complaint belongs to, and what it says. */
export type PasswordProblem = { on: 'password' | 'confirm'; text: string };

// The two rules HTML cannot express, once, for the three forms that set a
// password (reset, intake, Security). Each names the field it is about, so the
// message sits under that field and is tied to it (SH-13); both used to go red
// for either. A function beside the components that use it: nothing here holds
// state for Fast Refresh to lose.
// eslint-disable-next-line react-refresh/only-export-components
export const passwordProblem = (password: string, confirm: string): PasswordProblem | null =>
  password.length < MIN_PASSWORD
    ? { on: 'password', text: `Password must be at least ${MIN_PASSWORD} characters.` }
    : password !== confirm
      ? { on: 'confirm', text: 'Passwords don’t match.' }
      : null;

// The auth screens' call shape over the shared Field + TextInput
// (components/field.tsx). `error` is the message: it renders under the field
// with aria-invalid and aria-describedby. It was a flag that only turned the
// border rose, which told a screen reader nothing (SH-13).
export const Field = ({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  trailing,
  error,
  hint,
  autoFocus,
  required,
  max,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  trailing?: ReactNode;
  /** The message. A bare `true` only marks the field invalid, for a caller
   *  that says what is wrong somewhere else. */
  error?: boolean | string;
  hint?: ReactNode;
  autoFocus?: boolean;
  // `required` and `max` let these forms lean on native validation instead of a
  // hand-rolled validator: the browser blocks submit and says why itself, so no
  // submit button here is ever silently disabled. `max` is for type="date" (no
  // future birthdays).
  required?: boolean;
  max?: string;
}) => (
  <FormField label={label} error={typeof error === 'string' ? error : undefined} hint={hint}>
    <TextInput
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      required={required}
      max={max}
      aria-invalid={error ? true : undefined}
      trailing={trailing}
    />
  </FormField>
);

// Shared chrome: atmospheric backdrop, brand lockup, and the card below it.
export const AuthShell = ({ children }: { children: ReactNode }) => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--hf-space-5)',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div className="hf-atmosphere" />
    <main style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 408 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 'var(--hf-space-5)',
        }}
      >
        <img
          src={
            document.documentElement.dataset.theme === 'light'
              ? '/brand/hyprflow-logo-dark.png'
              : '/brand/hyprflow-logo.png'
          }
          alt="Hyprflow"
          style={{ height: 30, width: 'auto', display: 'block' }}
        />
        <span
          style={{
            fontFamily: 'var(--hf-font-mono)',
            fontSize: 'var(--hf-text-2xs)',
            color: 'var(--hf-ink-3)',
            letterSpacing: '0.08em',
            borderLeft: '1px solid var(--hf-rule-2)',
            paddingLeft: 'var(--hf-space-3)',
            marginLeft: 'var(--hf-space-3)',
          }}
        >
          OS
        </span>
      </div>
      {children}
    </main>
  </div>
);

// The glass card each of these screens is: a title, a line under it, and the
// form. A <form> when it has something to submit, so Enter submits and a
// password manager can pair the fields. It was four exported style objects
// spread by every screen.
export const AuthCard = ({
  title,
  sub,
  onSubmit,
  children,
}: {
  title: string;
  sub: ReactNode;
  onSubmit?: (e: FormEvent) => void;
  children: ReactNode;
}) => {
  const Tag = onSubmit ? 'form' : 'div';
  return (
    <Tag
      onSubmit={onSubmit}
      className="hf-glass"
      style={{ borderRadius: 'var(--hf-radius-card)', padding: 'var(--hf-space-6)' }}
    >
      <h1
        style={{
          margin: 0,
          fontFamily: 'var(--hf-font-display)',
          fontSize: 'var(--hf-text-2xl)',
          fontWeight: 'var(--hf-weight-medium)',
          letterSpacing: '-0.02em',
          color: 'var(--hf-ink)',
        }}
      >
        {title}
      </h1>
      <AuthNote style={{ margin: 'var(--hf-space-2) 0 var(--hf-space-5)' }}>{sub}</AuthNote>
      {children}
    </Tag>
  );
};

// Supporting copy on these screens: the line under a title, a note above a
// group of fields.
export const AuthNote = ({ children, style }: { children: ReactNode; style?: CSSProperties }) => (
  <p
    style={{
      margin: 0,
      fontSize: 'var(--hf-text-md)',
      lineHeight: 'var(--hf-leading-body)',
      color: 'var(--hf-ink-3)',
      ...style,
    }}
  >
    {children}
  </p>
);

export const PwToggle = ({ shown, onClick }: { shown: boolean; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={shown ? 'Hide password' : 'Show password'}
    // 23px from its padding alone, under WCAG 2.5.8's 24; 44 under a finger.
    className="hf-hit"
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 'var(--hf-space-5)',
      minHeight: 'var(--hf-space-5)',
      background: 'transparent',
      border: 0,
      color: 'var(--hf-ink-3)',
      cursor: 'pointer',
      padding: 'var(--hf-space-1)',
      borderRadius: 'var(--hf-radius-sm)',
    }}
  >
    {/* What pressing it does: an eye to show, the eye struck through to hide.
        The hidden state drew a padlock, read as "this field is locked" (SH-13). */}
    <Icon name={shown ? 'eyeOff' : 'eye'} size={15} />
  </button>
);
